import {
  DndContext as DragAndDropContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Active,
  type CollisionDetection,
  type DragOverEvent,
  type Over,
} from '@dnd-kit/core';
import {
  type CoreFeatures,
  type Row,
  type RowData,
} from '@tanstack/react-table';
import { type VirtualItem } from '@tanstack/react-virtual';
import { clsx } from 'clsx';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { getProperInertValue } from '../../../../../core/utils/get-inert-value.js';
import { devLog } from '../../../../../core/utils/logging.js';
import { modalityStore } from '../../../../../core/utils/modality.js';
import { Grid } from '../../../../../layouts/grid/Grid.js';
import { DataTableRow } from '../../../components/DataTableRow.js';
import {
  DATA_TABLE_ROW_HEIGHT_MAP,
  DATATABLE_COMPONENT_NAME,
} from '../../../constants.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import { RowDetailsElement } from '../../RowDetails/components/RowDetailsElement.js';
import type { DataTableThresholdsNormalizedRule } from '../../Thresholds/threshold-types.js';
import * as thresholdStyles from '../../Thresholds/Thresholds.sty.js';
import type { TDataTableFeatures } from '../../types/features.types.js';
import { dispatchDragTrackingEvent } from '../../utils/dnd-behavior-tracking-utils.js';
import {
  createRestrictToTableRowAreaModifier,
  createRowOrderCollisionDetection,
  getAllRowsInTableBody,
  getDragHandleForRow,
  getDragIndexes,
  resetAllStylesForRows,
  transformRowOnDragOver,
} from '../row-order-utils.js';
import * as styles from '../RowOrder.sty.js';

// Threshold for detecting sudden jumps caused by virtualizer scroll corrections
// vs. gradual user/auto-scroll during drag.
const SCROLL_JUMP_THRESHOLD = DATA_TABLE_ROW_HEIGHT_MAP.default * 10;

/**
 * Creates element to place in drag overlay.
 * @param overlayContainer - The container element that contains the row being dragged.
 * @param active - The row being dragged. If no corresponding row is found,
 *                 then undefined is returned from this function.
 * @param rows - all rows.
 * @param virtualColumns - virtual items for the columns
 * @param rowConfig - object containing all necessary row configurations to be able to replicate the row for the drag overlay
 * @returns element intended for overlay
 */
function createDraggingElement<TData extends RowData>(
  overlayContainer: RefObject<HTMLDivElement | null>,
  active: Active,
  rows: Row<TDataTableFeatures, TData>[],
  virtualColumns: VirtualItem[],
  rowConfig: {
    width: number;
    gridTemplateColumns: string;
    rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes';
    hasThresholds?: boolean;
    rowThresholds?: DataTableThresholdsNormalizedRule<TData>[];
    rowDetailsTemplate?: (params: { row: TData }) => ReactNode;
  },
): JSX.Element | undefined {
  const activeId = active.id as string;
  const draggingOriginalRow = active.data.current?.row as Row<
    TDataTableFeatures,
    TData
  >;
  const draggingVirtualRow = active.data.current?.virtualRow as VirtualItem;

  const {
    gridTemplateColumns,
    rowSeparation,
    hasThresholds,
    rowThresholds,
    rowDetailsTemplate,
  } = rowConfig;

  if (draggingOriginalRow === undefined || draggingVirtualRow === undefined) {
    devLog(
      `[DataTable] Dragging row or virtual row with row id ${activeId} is undefined.`,
    );
    return;
  }

  const isRowDetailsOpen = draggingOriginalRow.getIsDetailsOpen();

  const draggingVirtualDetailsRow = {
    ...draggingVirtualRow,
    index: draggingVirtualRow.index + 1,
  };

  return (
    <Grid
      ref={overlayContainer}
      gridTemplateColumns={gridTemplateColumns}
      padding={0}
      minWidth="max-content"
      gap={0}
      // TODO: React 19 update - inert is then in the IntrinsicAttributes and can be passed directly
      {...{ inert: getProperInertValue(true) }}
      className={clsx({
        [thresholdStyles.hasPillThresholds]:
          hasThresholds &&
          rowThresholds?.some((threshold) => threshold.type === 'pill'),
      })}
      style={{
        position: 'relative',
      }}
      data-dt-component={DATATABLE_COMPONENT_NAME}
    >
      <DataTableRow
        row={draggingOriginalRow as Row<TDataTableFeatures, RowData>}
        virtualRow={draggingVirtualRow}
        virtualItemIndex={0}
        rowCount={rows.length}
        virtualColumns={virtualColumns}
        isDraggedRow={false}
        isInDragOverlay={true}
        rowParity="even"
      />
      {isRowDetailsOpen ? (
        <RowDetailsElement
          originalRow={draggingOriginalRow}
          virtualRow={draggingVirtualDetailsRow}
          virtualItemIndex={1}
          rowSeparation={rowSeparation}
          rowParity="even"
        >
          {rowDetailsTemplate?.({
            row: draggingOriginalRow.original,
          })}
        </RowDetailsElement>
      ) : null}
    </Grid>
  );
}

/**
 * @internal
 */
export function RowOrderingDragAndDropContext<TData extends RowData>({
  children,
  rows,
  virtualColumns,
  rowSeparation,
}: Readonly<{
  children: ReactNode;
  rows: Row<TDataTableFeatures, TData>[];
  virtualColumns: VirtualItem[];
  rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes';
}>): JSX.Element | undefined {
  const table = useDataTable();
  const tableHeaderHeight = table.getTotalHeaderHeight();
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );
  const [draggingElement, setDraggingElement] = useState<ReactNode | undefined>(
    undefined,
  );
  const containedVariant = table.atoms.visualVariantConfig?.get().contained;
  const virtualizationContainer = table.getRowVirtualizationContainer();
  const tableGridElement =
    virtualizationContainer?.querySelector<HTMLElement>('[role=grid]');

  const overlayContainer = useRef<HTMLDivElement>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const dragHandleRef = useRef<HTMLElement | null>(null);

  const rowOrderCollisionDetection: CollisionDetection = useCallback(
    (args) =>
      createRowOrderCollisionDetection(table.atoms.draggingRowInfo.get())(args),
    [table],
  );

  const restrictToTableRowArea = useMemo(
    () =>
      createRestrictToTableRowAreaModifier(
        tableHeaderHeight,
        containedVariant,
        virtualizationContainer,
      ),
    [tableHeaderHeight, virtualizationContainer, containedVariant],
  );

  const rowOrderWhileDragging = useRef<string[]>([]);

  if (!tableGridElement || !virtualizationContainer || rows.length === 0) {
    return;
  }

  const overlayWidth = tableGridElement.parentElement?.clientWidth ?? 0;
  const originalRowOrder =
    table.atoms.rowOrder?.get() ?? rows.map((row) => row.id);

  const resetDragging = (): void => {
    // Remove scroll event listener
    if (virtualizationContainer && scrollHandlerRef.current) {
      virtualizationContainer.removeEventListener(
        'scroll',
        scrollHandlerRef.current,
      );
      scrollHandlerRef.current = null;
    }

    dispatchDragTrackingEvent(dragHandleRef.current, 'dragend');
    dragHandleRef.current = null;
    table.setDraggingRowInfo(null);
    setDraggingElement(undefined);
    resetAllStylesForRows(getAllRowsInTableBody(tableGridElement));
  };

  const handleOnDragStart = ({ active }: { active: Active }): void => {
    if (active.data.current) {
      const element = createDraggingElement<TData>(
        overlayContainer,
        active,
        rows,
        virtualColumns,
        {
          width: overlayWidth,
          gridTemplateColumns: table.getGridTemplateColumns(),
          rowSeparation,
          hasThresholds: table.options.hasThresholds,
          rowThresholds: table.options.rowThresholds,
          rowDetailsTemplate: table.options.rowDetailsTemplate,
        },
      );
      setDraggingElement(element);

      // Use requestAnimationFrame to delay execution until just before the next paint,
      // ensuring the DOM is fully updated. This allows us to sync the overlay's scroll
      // position with the table grid's scroll state.
      requestAnimationFrame(() => {
        if (overlayContainer.current?.parentElement) {
          overlayContainer.current.parentElement.scrollLeft =
            tableGridElement?.parentElement?.scrollLeft ?? 0;
        }
      });

      table.setDraggingRowInfo({
        id: active.id as string,
        index: active.data.current.rowIndex,
        row: active.data.current.row,
      });

      if (modalityStore.getState() !== 'keyboard') {
        const dragHandle = getDragHandleForRow(
          tableGridElement,
          active.data.current.rowIndex,
        );
        dragHandleRef.current = dragHandle;
        dispatchDragTrackingEvent(dragHandle, 'dragstart');
      }

      rowOrderWhileDragging.current = originalRowOrder
        ? [...originalRowOrder]
        : [];

      let lastScrollOffset = virtualizationContainer?.scrollTop ?? 0;

      // Attach scroll event listener to handle virtualization during drag
      const handleScroll = (): void => {
        if (!virtualizationContainer) {
          return;
        }

        const currentOffset = virtualizationContainer.scrollTop;
        const scrollDelta = Math.abs(currentOffset - lastScrollOffset);
        if (scrollDelta >= SCROLL_JUMP_THRESHOLD) {
          // Large sudden jump - virtualizer correction, prevent it
          virtualizationContainer.scrollTop = lastScrollOffset;
          return;
        }
        lastScrollOffset = currentOffset;
      };
      scrollHandlerRef.current = handleScroll;
      virtualizationContainer?.addEventListener('scroll', handleScroll);
    }
  };

  const handleOnDragEnd = ({
    active,
    over,
  }: {
    active: Active;
    over: Over | null;
  }): void => {
    if (over === undefined || over === null) {
      devLog('[Table] On drag end: over info is undefined or null', over);
      resetDragging();
      return;
    }

    const draggingRowInfo = table.atoms.draggingRowInfo.get();

    if (draggingRowInfo === null) {
      devLog(
        '[Table] On drag end: dragging row info is undefined',
        draggingRowInfo,
      );
      resetDragging();
      return;
    }

    // if dragged to the starting point, row order does not change
    if (over.id === active.id) {
      resetDragging();
      return;
    }

    const { fromIndex, toIndex } = getDragIndexes(
      originalRowOrder,
      active.id as string, // for us the IDs are always strings
      over.id as string, // for us the IDs are always strings
    );

    const newRowOrder = [...originalRowOrder];
    const activeRowSubRowsCount = draggingRowInfo.row.getLeafRows().length;
    const overRowSubRowsCount =
      (
        over.data.current?.row as Row<CoreFeatures, TData> | undefined
      )?.getLeafRows().length ?? 0;

    const rowsToMove = newRowOrder.splice(fromIndex, 1 + activeRowSubRowsCount);

    newRowOrder.splice(
      fromIndex < toIndex
        ? toIndex + overRowSubRowsCount - activeRowSubRowsCount
        : toIndex,
      0,
      ...rowsToMove,
    );
    table.setRowOrder(newRowOrder);
    resetDragging();

    // In Safari, after scrolling a table while dragging, the cells are weirdly shifted in the grid
    // We did not find why, probably a safari bug. This fixes the issue for now.
    virtualizationContainer?.scrollBy({ top: 1, behavior: 'smooth' });
    virtualizationContainer?.scrollBy({ top: -1, behavior: 'smooth' });
    // Always move focus to the dropped row's drag handle so the user can
    // continue navigating from the new position (e.g. via Tab).
    // For mouse/touch we pass `focusVisible: false` to suppress the focus ring.
    requestAnimationFrame(() => {
      const dragHandle = getDragHandleForRow(tableGridElement, toIndex);
      // Read at event time to avoid re-rendering all rows on modality changes.
      dragHandle?.focus(
        modalityStore.getState() !== 'keyboard'
          ? { focusVisible: false }
          : undefined,
      );
    });
  };

  return (
    <DragAndDropContext
      sensors={sensors}
      collisionDetection={rowOrderCollisionDetection}
      modifiers={[restrictToTableRowArea]}
      accessibility={{ container: virtualizationContainer }}
      onDragStart={handleOnDragStart}
      onDragEnd={handleOnDragEnd}
      onDragOver={(event: DragOverEvent) =>
        transformRowOnDragOver(
          tableGridElement,
          event,
          originalRowOrder,
          table.atoms.visualVariantConfig?.get().rowSeparation === 'none',
          table.atoms.draggingRowInfo.get()?.row,
        )
      }
      onDragCancel={() => resetDragging()}
    >
      {children}
      {
        // We need to portal the drag and drop overlay to escape CSS transforms on ancestor elements.
        // This ensures transforms on parent containers don't affect the position of the drag overlay.
        createPortal(
          <DragOverlay
            dropAnimation={null}
            className={styles.rowDndOverlay}
            style={{ width: overlayWidth, height: 'max-content' }}
          >
            {draggingElement}
          </DragOverlay>,
          document.body,
        )
      }
    </DragAndDropContext>
  );
}
